// Models
import { QueryRelationship, SingleEntityQueryType } from "../client/planner/EntityRelationships";
/**
 * Set of query types that support Trouter subscriptions for real-time updates.
 * When a query type is in this set, fetch requests can include a Trouter endpoint ID
 * to subscribe to updates for the queried entity or collection.
 *
 * Excludes:
 * - DependenciesOfTask: API not available yet
 * - DependencyById: API not available yet
 * - RosterById: API not available yet
 */ const TROUTER_SUPPORTED_QUERY_TYPES = new Set([
    // Collection relationships
    QueryRelationship.TasksInPlan,
    QueryRelationship.TasksInMyDayOfUser,
    QueryRelationship.TasksAssignedToUser,
    QueryRelationship.BucketsInPlan,
    QueryRelationship.GoalsInPlan,
    QueryRelationship.DependenciesInPlan,
    QueryRelationship.PlansInContainer,
    QueryRelationship.ChatMessagesForTask,
    // Single entity query types
    SingleEntityQueryType.PlanById,
    SingleEntityQueryType.TaskById,
    SingleEntityQueryType.BucketById,
    SingleEntityQueryType.GoalById
]);
/**
 * Checks if a query type supports Trouter subscriptions.
 * Use this to determine if a fetch request should include a Trouter endpoint ID.
 *
 * @param queryType - The query type to check
 * @returns true if the query type supports Trouter subscriptions
 */ export function isTrouterSubscriptionSupported(queryType) {
    return TROUTER_SUPPORTED_QUERY_TYPES.has(queryType);
}
/** Prefix for Trouter subscription failure responses */ const TROUTER_SUBSCRIPTION_FAILURE_PREFIX = "TrouterResourceSubscriptionFailed-";
/** Regex to extract retry-after seconds from TooBusy error code */ const RETRY_AFTER_REGEX = /TooBusy-RetryAfter(\d+)s/;
/**
 * Parses the Trouter subscription response header value.
 *
 * The viewUpdateKey is treated as an opaque string — the client does not parse its contents.
 * Success is determined by the absence of the failure prefix.
 * Failure format: `TrouterResourceSubscriptionFailed-<ErrorCode>` (e.g., "TrouterResourceSubscriptionFailed-TimeOut")
 *
 * @param headerValue The value of the X-Tasks-Subscribe-TrouterViewUpdateKey header
 * @returns Parsed result containing success status, view update key or error code, and retry information
 */ export function parseTrouterSubscriptionResponse(headerValue) {
    if (!headerValue) {
        return {
            success: false,
            errorCode: "NoResponseHeader",
            shouldRetry: true
        };
    }
    // Check if it's a failure response
    if (headerValue.startsWith(TROUTER_SUBSCRIPTION_FAILURE_PREFIX)) {
        const errorCode = headerValue.substring(TROUTER_SUBSCRIPTION_FAILURE_PREFIX.length);
        // Determine retry behavior based on error code
        let shouldRetry = false;
        let retryAfterSeconds;
        if (errorCode === "InvalidEndpoint" || errorCode === "NotSupported") {
            // Client errors or unsupported - don't retry
            shouldRetry = false;
        } else if (errorCode === "TimeOut") {
            // Timeout - retry on next request
            shouldRetry = true;
        } else if (errorCode.startsWith("TooBusy-RetryAfter")) {
            // Rate limiting - retry after specified delay
            shouldRetry = true;
            const match = errorCode.match(RETRY_AFTER_REGEX);
            if (match) {
                retryAfterSeconds = parseInt(match[1], 10);
            }
        } else {
            // UnexpectedError or other - maybe retry
            shouldRetry = true;
        }
        return {
            success: false,
            errorCode,
            shouldRetry,
            retryAfterSeconds
        };
    }
    // Any non-failure, non-empty value is a successful subscription with an opaque viewUpdateKey
    return {
        success: true,
        viewUpdateKey: headerValue,
        shouldRetry: false
    };
}
